import clsx from "clsx";
import { CSSProperties, ReactNode } from "react";

import { Button } from "@/components/atoms/buttons/Button";
import { CtaButtonTypeTemplate } from "@/states/page-editor/template-parts/cta-button";
import { getCorrectUrl } from "@/utils/common";
import { contentToHtml } from "@/utils/draft";

interface Props {
  part: CtaButtonTypeTemplate;
  formPath: string | undefined;
  onClick?: () => void;
  disabled?: boolean;
}

export default function CtaButton4Content({
  part,
  formPath,
  onClick,
  disabled,
}: Props) {
  const buttonHref = (): string => {
    if (part.buttonContent.link?.url) {
      return getCorrectUrl(part.buttonContent.link?.url);
    }
    if (formPath) {
      return formPath;
    }
    return "";
  };

  const isNewTab = !!(
    part.buttonContent.link?.isNewTab || part.buttonContent.service?.isNewTab
  );

  function ButtonRenderer({
    children,
    styles,
    disabled,
  }: {
    children: ReactNode;
    styles?: CSSProperties;
    disabled?: boolean;
  }) {
    const baseClassNames =
      "relative block text-white cursor-pointer md:transition disabled:pointer-events-none disabled:cursor-default font-bold";
    const largeDoubleArrow = [
      baseClassNames,
      "box-border !py-[13px] !px-8 !mx-auto !w-full min-h-[64px] rounded border-2 border-solid disabled:opacity-30 transition md:min-h-[72px] h-auto",
    ].join(" ");
    const linkRef = buttonHref();

    if (disabled) {
      return (
        <div className={largeDoubleArrow + " opacity-50"} style={styles}>
          {children}
        </div>
      );
    }

    return (
      <Button
        className={largeDoubleArrow}
        style={styles}
        href={linkRef}
        onClick={onClick}
        label={children}
        target={isNewTab ? "_blank" : undefined}
      ></Button>
    );
  }

  return (
    <div
      className="flex relative flex-col gap-4 py-[24px] px-[16px] rounded-[8px] border-2 md:p-[48px]"
      style={part.frameCssProperties}
    >
      {!!part.description?.content && (
        <div className="m-auto w-full">
          <div
            dangerouslySetInnerHTML={{
              __html: contentToHtml(part.description.content),
            }}
            className={clsx(
              "flex flex-col items-center min-h-[1.5em] text-center break-all",
              "text-[24px] font-bold leading-normal",
            )}
            style={part.description.textBaseStyle}
          ></div>
        </div>
      )}

      {!!part.buttonContent?.content && (
        <div className="m-auto w-full md:max-w-[400px]">
          <ButtonRenderer
            styles={part.buttonContent.cssProperties}
            disabled={disabled}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: contentToHtml(part.buttonContent.content),
              }}
              className="flex flex-col min-h-[1.5em] text-xl font-bold leading-normal text-center md:text-2xl"
              style={part.buttonContent.textBaseStyle}
            />
          </ButtonRenderer>
        </div>
      )}
    </div>
  );
}
